import {useEffect, useState} from 'react';

import {AuthSession} from '../App';
import {
    SessionExpiredError,
    TempAccessRequest,
    TwingateUnreachableError,
    fetchRequests,
} from '../lib/api';
import {GrantView} from '../lib/grant-view';
import {makeTenantChip, makeUserChip, timeLeftLabel} from '../lib/request-chips';

import {AccessChip} from './AccessChip';
import {Spinner} from './Spinner';
import {UserChip} from './UserChip';

const HISTORY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface Props {
    open: boolean;
    request: TempAccessRequest | null;
    grants: GrantView[];
    session: AuthSession;
    onClose: () => void;
}

interface HistoryState {
    loading: boolean;
    error?: string;
    /** All requests in the 30d window for the same username + shardId. */
    items?: TempAccessRequest[];
}

/**
 * Side drawer opened from a Requests row. Shows the clicked grant's details
 * up top and a 30-day history of grants by the same user against the same
 * tenant underneath. The history is fetched on open (no cache) — the lookup
 * is small (one Insights query per click) and the user usually opens just a
 * handful of rows per session.
 */
export function RequestDetailDrawer({open, request, grants, session, onClose}: Props) {
    const [history, setHistory] = useState<HistoryState>({loading: false});

    useEffect(() => {
        if (!open || !request) {
            return;
        }
        let cancelled = false;
        setHistory({loading: true});
        const since = Date.now() - HISTORY_WINDOW_MS;
        fetchRequests(session.idToken, {since, limit: 1000})
            .then(resp => {
                if (cancelled) {
                    return;
                }
                // Match a "previous grants for this combo" row: same shard
                // AND same recipient. DA events identify the recipient by
                // username, Impers events by email — match whichever the
                // current row carries.
                const items = resp.requests.filter(r => {
                    if (r.shardId !== request.shardId) {
                        return false;
                    }
                    if (request.username) {
                        return r.username === request.username;
                    }
                    if (request.email) {
                        return r.email === request.email;
                    }
                    return false;
                });
                setHistory({loading: false, items});
            })
            .catch(err => {
                if (cancelled) {
                    return;
                }
                if (err instanceof SessionExpiredError) {
                    // App's handler already signed us out; the drawer will
                    // unmount when the login screen replaces the shell.
                    setHistory({loading: false});
                    return;
                }
                const msg =
                    err instanceof TwingateUnreachableError
                        ? 'Twingate unreachable'
                        : err instanceof Error
                        ? err.message
                        : String(err);
                setHistory({loading: false, error: msg});
            });
        return () => {
            cancelled = true;
        };
    }, [open, request, session.idToken]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!request) {
        return (
            <>
                <div className={`drawer-backdrop${open ? ' open' : ''}`} onClick={onClose} aria-hidden />
                <aside className={`drawer${open ? ' open' : ''}`} aria-hidden />
            </>
        );
    }

    // Impersonation rows carry email directly; DA rows need a username→email
    // lookup from the IAM grants list.
    const email = request.email ?? (request.username ? emailFor(request.username, grants) : undefined);
    const tenantName = grants.find(g => g.resource === request.shardId)?.resourceName;
    const userChip = makeUserChip(request, email);
    const tenantChip = makeTenantChip(request, tenantName);
    const historyItems = (history.items ?? []).filter(r => r.policyTimestamp !== request.policyTimestamp);

    return (
        <>
            <div className={`drawer-backdrop${open ? ' open' : ''}`} onClick={onClose} aria-hidden />
            <aside className={`drawer${open ? ' open' : ''}`} role="dialog" aria-modal aria-hidden={!open}>
                <div className="drawer-head">
                    <div style={{flex: 1, minWidth: 0}}>
                        <div className="title">Temp access request</div>
                        <div className="sub">{formatAbsolute(request.grantedAt)}</div>
                    </div>
                    <button className="btn-icon" onClick={onClose} type="button" aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M6 6l12 12M18 6L6 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                <div className="drawer-body">
                    <section className="drawer-section">
                        <h4>User</h4>
                        <div className="access-chips">
                            <UserChip chip={userChip} hideBadge />
                        </div>
                    </section>

                    <section className="drawer-section">
                        <h4>Tenant</h4>
                        <div className="access-chips">
                            <AccessChip chip={tenantChip} hideBadge />
                        </div>
                    </section>

                    <section className="drawer-section">
                        <h4>Time left</h4>
                        <div>{timeLeftLabel(request)}</div>
                    </section>

                    <section className="drawer-section">
                        <h4>Reason</h4>
                        <div style={{whiteSpace: 'pre-wrap'}}>{request.reason}</div>
                    </section>

                    <section className="drawer-section">
                        <h4>Recent history (last 30 days, same user & tenant)</h4>
                        {history.loading ? (
                            <Spinner label="Loading history…" />
                        ) : history.error ? (
                            <div className="empty-opt" style={{padding: '12px 0'}}>
                                Failed to load: {history.error}
                            </div>
                        ) : historyItems.length === 0 ? (
                            <div className="empty-opt" style={{padding: '12px 0'}}>
                                No other requests in the last 30 days.
                            </div>
                        ) : (
                            historyItems.map(r => (
                                <div className="drawer-row" key={r.policyTimestamp}>
                                    <div style={{minWidth: 0, flex: 1}}>
                                        <div className="res">{formatAbsolute(r.grantedAt)}</div>
                                        <div className="meta">{r.reason}</div>
                                    </div>
                                    <span className="when">{formatRelative(r.grantedAt)}</span>
                                </div>
                            ))
                        )}
                    </section>
                </div>
            </aside>
        </>
    );
}

function emailFor(username: string, grants: GrantView[]): string | undefined {
    for (const g of grants) {
        const u = (g.raw as {userName?: string} | undefined)?.userName;
        if (u === username && g.email) {
            return g.email;
        }
    }
    return undefined;
}

/** Synthesize a UserChip from a TempAccessRequest — Temp grants are data-access, 24h, IAM. */

function formatRelative(iso: string): string {
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) {
        return iso;
    }
    const diff = Date.now() - t;
    if (diff < 60_000) {
        return 'just now';
    }
    if (diff < 3_600_000) {
        return `${Math.round(diff / 60_000)}m ago`;
    }
    if (diff < 86_400_000) {
        return `${Math.round(diff / 3_600_000)}h ago`;
    }
    return `${Math.round(diff / 86_400_000)}d ago`;
}

function formatAbsolute(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
